import React, { useEffect, useState } from 'react';
import { feeStatsService, feeReminderService } from '../../services/api';
import { formatFcfa } from '../../utils/feeUtils';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { FiMessageCircle, FiClock, FiX, FiSend, FiList } from 'react-icons/fi';
import Swal from 'sweetalert2';

const STATUS_LABELS = {
  sent: { label: 'Envoyé', className: 'bg-green-100 text-green-800' },
  link_opened: { label: 'Lien WhatsApp', className: 'bg-blue-100 text-blue-800' },
  failed: { label: 'Échec', className: 'bg-red-100 text-red-800' },
  pending: { label: 'En attente', className: 'bg-gray-100 text-gray-800' },
};

const openWaUrls = (results) => {
  const urls = results.flatMap((r) =>
    (r.results || []).filter((item) => item.wa_url).map((item) => item.wa_url)
  );
  urls.forEach((url, index) => {
    setTimeout(() => window.open(url, '_blank', 'noopener,noreferrer'), index * 400);
  });
  return urls.length;
};

const FeeOverduePage = () => {
  const { selectedYear } = useAcademicYear();
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchOverdue = async () => {
    setLoading(true);
    const params = selectedYear ? { annee_scolaire_id: selectedYear.id } : {};
    const res = await feeStatsService.getOverdue(params);
    setOverdue(res.data.data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchOverdue();
  }, [selectedYear]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    const res = await feeReminderService.getHistory({ limit: 50 });
    setHistory(res.data.data || []);
    setHistoryLoading(false);
  };

  const handleShowHistory = () => {
    setShowHistory(true);
    fetchHistory();
  };

  const handleNotify = async (row) => {
    if (!row.whatsapp_count) {
      Swal.fire({
        icon: 'warning',
        title: 'Aucun WhatsApp',
        text: 'Aucun parent avec numéro WhatsApp enregistré pour cet élève.',
      });
      return;
    }

    const previewRes = await feeReminderService.preview(row.inscription_id);
    const previews = previewRes.data.data?.previews || [];
    const apiConfigured = previewRes.data.data?.api_configured;

    const contactsList = previews.map((p) =>
      `• ${p.contact.parent_prenom} ${p.contact.parent_nom} (${p.contact.phone})`
    ).join('<br>');

    const confirm = await Swal.fire({
      icon: 'question',
      title: 'Envoyer une relance WhatsApp ?',
      html: `
        <p class="text-left mb-2"><strong>${row.prenom} ${row.nom}</strong> — ${formatFcfa(row.overdue_amount)}</p>
        <p class="text-left text-sm text-gray-600 mb-2">Destinataires :</p>
        <p class="text-left text-sm">${contactsList}</p>
        ${!apiConfigured ? '<p class="text-left text-xs text-amber-600 mt-3">Mode manuel : WhatsApp s\'ouvrira avec le message pré-rempli.</p>' : ''}
      `,
      showCancelButton: true,
      confirmButtonText: 'Envoyer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#16a34a',
    });

    if (!confirm.isConfirmed) return;

    setSending(true);
    try {
      const res = await feeReminderService.send({ inscription_id: row.inscription_id });
      const results = res.data.results || [];

      if (!apiConfigured) {
        const opened = openWaUrls([res.data]);
        await Swal.fire({
          icon: 'success',
          title: 'Relance préparée',
          text: `${opened} lien(s) WhatsApp ouvert(s). L'envoi a été historisé.`,
        });
      } else if (res.data.success) {
        await Swal.fire({ icon: 'success', title: 'Notification envoyée', timer: 2000, showConfirmButton: false });
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: results.find((r) => r.error)?.error || res.data.message || 'Échec de l\'envoi',
        });
      }
      fetchOverdue();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: err.response?.data?.message || 'Impossible d\'envoyer la notification',
      });
    } finally {
      setSending(false);
    }
  };

  const handleNotifyAll = async () => {
    const withWhatsapp = overdue.filter((o) => o.whatsapp_count > 0);
    if (withWhatsapp.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Aucun destinataire',
        text: 'Aucun élève impayé n\'a de parent avec numéro WhatsApp.',
      });
      return;
    }

    const confirm = await Swal.fire({
      icon: 'question',
      title: 'Relance groupée',
      text: `Envoyer une relance WhatsApp pour ${withWhatsapp.length} élève(s) ?`,
      showCancelButton: true,
      confirmButtonText: 'Envoyer tout',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#16a34a',
    });

    if (!confirm.isConfirmed) return;

    setSending(true);
    try {
      const res = await feeReminderService.sendBulk({
        inscription_ids: withWhatsapp.map((o) => o.inscription_id),
      });

      const opened = openWaUrls(res.data.data || []);
      await Swal.fire({
        icon: res.data.success ? 'success' : 'warning',
        title: 'Relances traitées',
        html: `
          <p>${res.data.success_count || 0} envoyée(s)</p>
          <p>${res.data.skip_count || 0} ignorée(s) (pas de WhatsApp)</p>
          ${opened > 0 ? `<p class="text-sm text-gray-600 mt-2">${opened} lien(s) WhatsApp ouvert(s)</p>` : ''}
        `,
      });
      fetchOverdue();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: err.response?.data?.message || 'Impossible d\'envoyer les relances',
      });
    } finally {
      setSending(false);
    }
  };

  const totalOverdue = overdue.reduce((sum, o) => sum + (parseInt(o.overdue_amount) || 0), 0);
  const withWhatsappCount = overdue.filter((o) => o.whatsapp_count > 0).length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">Suivi des impayés</h1>
          <p className="text-gray-600">
            {overdue.length} élève(s) en retard — Total : <strong className="text-red-600">{formatFcfa(totalOverdue)}</strong>
            {withWhatsappCount > 0 && (
              <span className="ml-2 text-green-700">· {withWhatsappCount} avec WhatsApp</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleShowHistory}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <FiList className="h-4 w-4" />
            Historique
          </button>
          <button
            type="button"
            onClick={handleNotifyAll}
            disabled={sending || withWhatsappCount === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiSend className="h-4 w-4" />
            Relancer tout (WhatsApp)
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Élève</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matricule</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Classe</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant impayé</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Échéance</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Retard</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">WhatsApp</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {overdue.map((o) => (
                  <tr key={o.inscription_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium">{o.nom} {o.prenom}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{o.matricule}</td>
                    <td className="px-4 py-3 text-sm">{o.classe}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-red-600">{formatFcfa(o.overdue_amount)}</td>
                    <td className="px-4 py-3 text-sm">{new Date(o.oldest_due_date).toLocaleDateString('fr-FR')}</td>
                    <td className="px-4 py-3 text-sm text-right">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        o.days_overdue > 90 ? 'bg-red-100 text-red-800' :
                        o.days_overdue > 60 ? 'bg-orange-100 text-orange-800' :
                        o.days_overdue > 30 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {o.days_overdue} j
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {o.whatsapp_count > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" title={
                          (o.whatsapp_contacts || []).map((c) => `${c.prenom} ${c.nom}: ${c.phone}`).join(', ')
                        }>
                          <FiMessageCircle className="h-3 w-3" />
                          {o.whatsapp_count}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                      {o.notification_count > 0 && (
                        <div className="text-[10px] text-gray-500 mt-1 flex items-center justify-center gap-0.5">
                          <FiClock className="h-3 w-3" />
                          {o.notification_count}x
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => handleNotify(o)}
                        disabled={sending || !o.whatsapp_count}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <FiMessageCircle className="h-3.5 w-3.5" />
                        Notifier
                      </button>
                    </td>
                  </tr>
                ))}
                {overdue.length === 0 && (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-green-600">Aucun impayé — excellent !</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal historique */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Historique des notifications WhatsApp</h2>
              <button type="button" onClick={() => setShowHistory(false)} className="p-2 rounded-lg hover:bg-gray-100">
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              {historyLoading ? (
                <div className="p-8 text-center">Chargement...</div>
              ) : history.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Aucune notification envoyée</p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Élève</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Parent</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Envoyé par</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {history.map((h) => {
                      const status = STATUS_LABELS[h.status] || STATUS_LABELS.pending;
                      return (
                        <tr key={h.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap">
                            {new Date(h.sent_at).toLocaleString('fr-FR')}
                          </td>
                          <td className="px-3 py-2">
                            {h.eleve_prenom} {h.eleve_nom}
                            {h.matricule && <span className="text-gray-400 text-xs ml-1">({h.matricule})</span>}
                          </td>
                          <td className="px-3 py-2">{h.parent_prenom} {h.parent_nom}</td>
                          <td className="px-3 py-2 font-mono text-xs">{h.phone_number}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}>
                              {status.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-500">
                            {h.sender_prenom ? `${h.sender_prenom} ${h.sender_nom}` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeOverduePage;
